import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PackageCheck, Loader2, CheckCircle2, Truck, Clock, MapPin } from 'lucide-react';
import { parcelsApi, type PublicParcelTracking, type ParcelStatus } from '../services/api';

const STATUS_COLORS: Record<ParcelStatus, string> = {
  CREATED: 'bg-gray-100 text-gray-700',
  ASSIGNED: 'bg-blue-100 text-blue-700',
  OUT_FOR_DELIVERY: 'bg-amber-100 text-amber-800',
  DELIVERED: 'bg-green-100 text-green-700',
  PARTIALLY_DELIVERED: 'bg-lime-100 text-lime-700',
  REFUSED: 'bg-red-100 text-red-700',
  ABSENT: 'bg-orange-100 text-orange-700',
  RETURNED: 'bg-purple-100 text-purple-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
};

export default function PublicParcelTrackPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicParcelTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setNotFound(true); setLoading(false); return; }
      try {
        const res = await parcelsApi.publicTrack(token);
        if (!cancelled) setData(res);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '—';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 justify-center mb-6 text-primary-600">
          <PackageCheck className="w-7 h-7" />
          <span className="text-xl font-bold">{data?.carrier || t('parcels.track.title')}</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : notFound || !data ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            {t('parcels.track.not_found')}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-gray-500">{data.reference}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[data.status]}`}>
                {t(`parcels.status.${data.status}`)}
              </span>
            </div>

            {data.recipientName && (
              <div className="text-gray-900 font-medium">{data.recipientName}{data.city ? ` · ${data.city}` : ''}</div>
            )}

            {data.status === 'DELIVERED' || data.status === 'PARTIALLY_DELIVERED' ? (
              <div className="flex items-start gap-2 text-green-700 bg-green-50 rounded-lg p-3 text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <div>
                  <div className="font-medium">{t('parcels.track.delivered_on', { date: fmt(data.deliveredAt) })}</div>
                  {data.signerName && <div className="text-green-800">{t('parcels.track.signed_by', { name: data.signerName })}</div>}
                </div>
              </div>
            ) : data.scheduledDate ? (
              <div className="flex items-center gap-2 text-amber-800 bg-amber-50 rounded-lg p-3 text-sm">
                <Truck className="w-5 h-5 shrink-0" />
                <span>{t('parcels.track.planned', { date: fmt(data.scheduledDate) })}</span>
              </div>
            ) : null}

            {/* Timeline */}
            {data.events.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-400 uppercase mb-2">{t('parcels.track.timeline')}</div>
                <ol className="relative border-l border-gray-200 ml-2 space-y-3">
                  {data.events.slice().reverse().map((e, i) => (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-1.5 mt-1 w-3 h-3 rounded-full bg-primary-500" />
                      <div className="text-sm text-gray-900">{t(`parcels.track.event.${e.type}`, e.type)}</div>
                      <div className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(e.at)}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="text-xs text-gray-400 flex items-center gap-1 pt-2 border-t border-gray-100">
              <MapPin className="w-3 h-3" /> {t('parcels.track.footer')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
